import { gql as parse } from '@apollo/client/core';
import { StyleSheet } from '@react-pdf/renderer';
import React from 'react';
import { Debug, Wine } from '../../pdf-design-system';
import messages from '../../pdf-design-system/locale/messages';
import { ThemeProvider } from '../../pdf-design-system/useTheme';
import UserLocaleProvider from '../../Providers/UserLocaleProvider';
import Page1 from './BarcelonaBrandsInfoSheetPage1Final';
import Page2 from './BarcelonaBrandsInfoSheetPage2Final';
import futura400 from './fonts/Futura400';
import futura500 from './fonts/Futura500';
import futura600 from './fonts/Futura600';
import futura700 from './fonts/Futura700';

/**
 * The Layout component is responsible for rendering one or multiple pages.
 * It sets the page size and sets up the wrapping.
 * In this case we have multiple items per page that might wrap, so we add a single page only.
 * It renders the page header and footer, and a list of sections in between.
 * A closing spacer ensures that the footer is on the page bottom,
 * without resorting to absolute positioning.
 * The stencil will be placed correctly if we use `padding` for the page margins.
 */
export default function BarcelonaBrandsInfoSheet({
  data,
  orientation = 'LEFT',
  variant,
  extend,
  locale,
  ...rest
}) {
  const product = afterQuery(data);
  return (
    <ThemeProvider
      theme={BarcelonaBrandsInfoSheet.theme}
      orientation={orientation}
    >
      <UserLocaleProvider locale={locale} messages={messages}>
        <Wine.Provider value={product}>
          <Page1 product={product} {...rest} />
          <Page2 product={product} {...rest} />
        </Wine.Provider>
      </UserLocaleProvider>
    </ThemeProvider>
  );
}

// We need to trick the IDE so we don't see errors for unknown fragments.
BarcelonaBrandsInfoSheet.fragment = parse/* GraphQL */ `
  fragment BarcelonaBrandsInfoSheetInfoSheet on Product {
    productId
    ...BarcelonaBrandsInfoSheetInfoSheetPage1
    ...BarcelonaBrandsInfoSheetPage2

    # Components
    ...Debug
  }

  ${Debug.fragment}
  ${Page1.fragment}
  ${Page2.fragment}
`;

function afterQuery(product) {
  return {
    ...product,
  };
}

BarcelonaBrandsInfoSheet.theme = StyleSheet.create({
  components: {
    Label: {
      base: {
        ...futura400,
        textTransform: 'uppercase',
      },
      larger: {
        fontWeight: 400,
        fontSize: 11,
      },
      summary: {
        ...futura700,
        borderTop: '2px solid black',
        borderBottom: '2px solid black',
        textAlign: 'center',
        paddingTop: 4,
        fontSize: 9,
        marginBottom: 7,
      },
      default: {
        ...futura500,
        fontSize: 8,
      },
    },
    Link: {
      base: {
        ...futura400,
        lineHeight: 1.4,
        color: 'black',
        textDecoration: 'none',
      },
    },
    SemiTitle: {
      base: {
        ...futura700,
      },
      default: {
        fontSize: 11,
      },
      smaller: {
        fontSize: 9,
      },
    },
    Text: {
      base: {
        ...futura400,
      },
      description: {
        ...futura600,
        fontSize: 13,
      },
      larger: {
        fontSize: 14,
      },
      default: {
        fontSize: 11,
      },
      smaller: {
        fontSize: 8,
      },
    },
    Title: {
      base: {
        ...futura600,
        textTransform: 'uppercase',
      },
      default: {
        fontSize: 19.5,
      },
      smaller: {
        fontSize: 16,
      },
      smallest: {
        fontWeight: 500,
        fontSize: 13,
      },
    },
    DynamicComponent: {
      default: {
        horizontal: StyleSheet.create({
          component: {
            flexDirection: 'row',
            paddingVertical: 3,
            alignItems: 'center',
          },
          label: { ...futura400, fontSize: 11, width: '28%' },
          value: {
            ...futura400,
            fontSize: 11,
          },
        }),
        vertical: StyleSheet.create({
          component: {
            flexDirection: 'column',
            marginBottom: 10,
          },
          label: {},
          value: {},
        }),
      },
      summary: {
        horizontal: StyleSheet.create({
          component: {
            flexDirection: 'column',
            paddingVertical: 3,
            alignItems: 'center',
          },
          label: {
            width: '60%',
            fontSize: 10,
            lineHeight: 1,
            textTransform: 'none',
          },
          value: {
            width: '40%',
            lineHeight: 1.1,
            fontSize: 9,
            paddingVertical: 1,
          },
        }),
        vertical: StyleSheet.create({
          component: {
            flexDirection: 'column',
            marginBottom: 10,
          },
          label: {},
          value: {},
        }),
      },
      logistics: {
        horizontal: StyleSheet.create({
          component: {
            flexDirection: 'row',
            paddingVertical: 3,
            alignItems: 'center',
          },
          label: { fontWeight: 400, fontSize: 11, width: 180 },
          value: {
            ...futura400,
            fontSize: 11,
          },
        }),
        vertical: StyleSheet.create({
          component: {
            flexDirection: 'column',
            marginBottom: 10,
          },
          label: {},
          value: {},
        }),
      },
    },
  },
});
